import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed, effect,
  ElementRef, HostBinding,
  inject, input,
  OnDestroy,
  output, signal,
  viewChild,
  ViewEncapsulation
} from '@angular/core';
import { createUid, IDType } from '@core/models/types';
import { isNullishArray } from '@utils/array';
import { BooleanInputType, toBooleanProperty } from '@utils/boolean/transform';
import { clearTimeouts } from '@utils/clear-timeouts';
import { stopAndPreventEvent } from '@utils/dom/event';
import { µsetTimeout } from '@utils/function/timer';
import { isString } from '@utils/type/is-string';
import { MirIcon } from '@widgets/mir-icon';

const FIT_CONTENT = `fit-content`;

const BaseCss = `group/MainContainer
  w-full
  inline-flex
  items-center
  bg-white
  rounded
  ring-1
  ring-gray-300
  overflow-hidden
  max-w-full
  hover:ring-blue-400
  focus-in:ring-blue-400
  hover:bg-gray-50
  transition-all`;

const SelectSize: { [key in SelectSizeType]: string; } = {
  'sm': 'h-8',
  'normal': 'h-10',
  'lg': 'h-12'
};

const TextSize: { [key in SelectSizeType]: string; } = {
  'sm': 'text-sm',
  'normal': 'text-base',
  'lg': 'text-lg'
};

const SelectWaitIcon = `❔`;

export interface MirOption {
  id: IDType;
  name: string;
  label: string;
  selected: boolean;
  disabled: boolean;
  index?: number;
  value?: unknown;
  data?: unknown;
  icon?: string;
}

export interface MirSelectEvent {
  event: Event,
  option: MirOption;
}

export type SelectSizeType = 'sm' | 'normal' | 'lg';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: 'mir-select',
  template: `
    <button #MainButton
        class="relative z-50
                size-full
                pl-4 pr-2
                inline-flex
                !gap-x-[1ch]
                justify-between items-center
                font-inherit font-semibold
                text-nowrap
                select-none outline-none
                cursor-pointer"
        [attr.id]="buttonID"
        [attr.popovertarget]="popoverID">
      <span class="truncate">{{currentLabel()}}</span>
      <MirIcon>expand_more</MirIcon>
    </button>

    <ul #Options
        popover
        [attr.id]="popoverID"
        [ngStyle]="popoverStyle"
        class="group/SelectOptionsUL
                Glassylicious smallDistorsionGlass
                z-40 overflow-x-hidden overflow-y-auto
                absolute
                top-[anchor(bottom)]
                left-[anchor(left)]
                -translate-y-4
                w-[--options-container-width]
                min-w-[--options-container-min-width]
                max-h-[clamp(24rem,_40vh,_50vh)]
                my-2
                flex-col
                justify-start items-center
                rounded
                shadow-16
                scroll-thin
                font-inherit">
      @for (option of options(); track option.id) {
        <li #Option
            class="group/OptionLI
                    overflow-hidden
                    w-full h-8
                    block
                    font-inherit"
            [class.selected]="option.id === current().id"
            [class.disabled]="option.disabled">
          <button
              class="size-full
                      px-4 py-2
                      inline-flex
                      items-center
                      gap-x-[0.5ch]
                      text-left text-[inherit]
                      font-inherit
                      truncate
                      select-none outline-none
                      cursor-pointer
                      hover:bg-blue-400
                      hover:text-white
                      group-[.selected]/OptionLI:text-white
                        group-[.selected]/OptionLI:bg-blue-400
                        group-[.selected]/OptionLI:font-bold
                        group-[.selected]/OptionLI:cursor-default
                        group-[.selected]/OptionLI:pointer-events-none"
              [disabled]="option.disabled"
              (click)="handleSelectOption($event, option)">
            @if (option.icon) {
              <MirIcon size="sm">{{option.icon}}</MirIcon>
            }
            {{option.label ?? option.name}}
          </button>
        </li>
      }
    </ul>
  `,
  imports: [
    CommonModule,
    MirIcon
  ]
})
export class MirSelectComponent<T extends MirOption[]> implements OnDestroy {

  @HostBinding('class')
  get cssHostClass(): string {
    const sizeKey = this.#size as string;
    return `${BaseCss} ${TextSize[sizeKey]} ${SelectSize[sizeKey]} `;
  }

  @HostBinding('style')
  get selectAnchorName(): string { return this.#selectAnchorName(); }

  @HostBinding('id')
  get hostID(): string { return this.#selectID; }

  /** @Input */
  readonly options = input.required<T>();

  readonly fitContent = input('minParent', {
    transform: (value: BooleanInputType) => ((isString(value) && value === 'minParent') ? value : toBooleanProperty(value))
  });

  /** @Output */
  readonly selectionChange = output<MirSelectEvent>();

  /** @ViewChild */
  readonly optionsContainer = viewChild<ElementRef>('Options');

  /** @Inject */
  readonly #elementRef = inject(ElementRef);

  popOverCallBack = (event: Event) => { this.#onClickOutside(event); };

  #uid = createUid();
  #selectID = `mir-select-${this.#uid}`;
  #optionsPopoverID = `mir-select-popover-${this.#uid}`;
  #timerID: number;
  #size: SelectSizeType = 'normal';
  readonly #anchorName = `toggle-select-${this.#uid}`;

  protected readonly elementMinWidth = signal(`${FIT_CONTENT}`);
  protected readonly elementWidth = signal(`${FIT_CONTENT}`);
  protected readonly current = signal<MirOption>(null);
  protected readonly open = signal<boolean>(false);
  protected readonly buttonID = `mir-select-btn-${this.#uid}`;
  protected readonly buttonStyle = { 'anchor-name': `--${this.#anchorName}` };
  protected readonly popoverStyle = {
    'position-anchor': `--${this.#anchorName}`,
    'transition-behavior': 'allow-discrete',
    'inset-area': 'span-x-start bottom',
    'position-try-options': 'flip-block, flip-inline',
    'translate': '0% -1%',
    '--glass-container-box-shadow': 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)'
  };

  protected readonly currentLabel = computed(() => {
    const current = this.current();
    return current.label ?? current.name ?? SelectWaitIcon;
  });

  readonly #selectAnchorName = computed(() => `anchor-name: --${this.#anchorName}; --options-container-min-width: ${this.elementMinWidth()}; --options-container-width: ${this.elementWidth()}`);

  constructor() {
    this.current.set({
      id: -1,
      index: -1,
      value: null,
      name: null,
      label: '',
      selected: false,
      disabled: true
    });

    effect(() => {
      const options = this.options();

      if (options) {
        this.#checkData();
        this.#checkUI();
        this.#updateUI();
      }
    });
  }

  get popoverID(): string { return this.#optionsPopoverID; }

  ngOnDestroy() {
    this.#closePopover();
    clearTimeouts([this.#timerID]);
  }

  handleClick(event: Event) {
    stopAndPreventEvent(event);
    this.open.set(!this.open());
  }

  handleSelectOption(event: Event, option: MirOption) {
    this.current.set(option);
    this.#closePopover();
    this.selectionChange.emit({ event, option });
  }

  #onClickOutside(event: Event) {
    const nativeElement = this.optionsContainer().nativeElement;
    if (!nativeElement.contains(event.target)) { this.#closePopover(); }
  }

  #checkData() {
    const options = this.options();

    if (!isNullishArray(options)) {
      const selected = options.find(option => option.selected);
      if (selected) { this.current.set(selected); }
    }
  }

  #checkUI() {
    const nativeElement = this.#elementRef.nativeElement;

    this.#size = (nativeElement.getAttribute('sm') !== null || nativeElement.getAttribute('small') !== null)
      ? 'sm'
      : (nativeElement.getAttribute('lg') !== null || nativeElement.getAttribute('large') !== null)
        ? 'lg'
        : 'normal';
  }

  #updateUI() {
    this.#timerID = µsetTimeout(() => {
      const nativeElement = this.#elementRef.nativeElement;
      const widthValue = `${nativeElement.clientWidth}px`;
      const width = `${this.fitContent() ? FIT_CONTENT : widthValue}`;
      const minWidth = `${this.fitContent() === 'minParent' ? widthValue : this.fitContent() ? FIT_CONTENT : widthValue}`;
      this.elementMinWidth.set(minWidth);
      this.elementWidth.set(width);
    });
  }

  #closePopover() {
    this.optionsContainer().nativeElement.hidePopover();
    this.open.set(false);
  }
}
